import React, { useState } from 'react';
import Input from '@welldev-group/rcl-input';
import Button from '@welldev-group/rcl-button';
import SelectBox from '@welldev-group/rcl-select-box';
import { I18nText } from '@welldev-group/react-comp-lib';
import Icon from '@welldev-group/rcl-icon';
import Modal from '@welldev-group/rcl-modal';
import PropTypes from 'prop-types';
import { debounce } from 'throttle-debounce';

import PermissionsSelection from './PermissionsSelection';

import httpClient from '../../../shared/libraries/httpClient';
import routes from '../../../shared/routes';

const EditTable = props => {
  const {
    userInitialAttributes,
    previousSelectedTeam,
    affiliateId,
    permissionOptions,
    permissionTemplateOptions,
    submitURL,
    currentUserId,
    signedInAsSuperAdmin,
    isVisible,
    onModalClose,
    fetchUsers
  } = props;

  const [firstName, setFirstName] = useState(userInitialAttributes.firstName);
  const [lastName, setLastName] = useState(userInitialAttributes.lastName);
  const [email, setEmail] = useState(userInitialAttributes.email);
  const [password, setPassword] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(previousSelectedTeam);
  const [permissions, setPermissions] = useState(
    userInitialAttributes.permissions,
  );
  const [error, setError] = useState({});

  const fetchTeams = debounce(500, false, async inputValue => {
    if (inputValue.length > 1) {
      const { data } = await httpClient.get(
        routes.teams.list({ q: inputValue, affiliate_id: affiliateId }),
      );

      const teamOptions = data.teams.map(team => ({
        label: team.name,
        value: team.id,
      }));
      setTeams(teamOptions);
    }
  });

  const onTeamChange = selectedOption => {
    setSelectedTeam(selectedOption);
  };

  const isEditingSelf = currentUserId === userInitialAttributes.id;
  const isEditingSuperAdmin =
    !isEditingSelf && userInitialAttributes.superAdmin;

  const updateUser = async () => {
    const updateUserParams = {
      first_name: firstName,
      last_name: lastName,
      password,
      email,
      affiliate_id: affiliateId,
      team_ids: teams,
    };

    if (!isEditingSelf) {
      updateUserParams.permissions = permissions;
    }

    const { data } = await httpClient.put(
      submitURL({ id: userInitialAttributes.id }),
      {
        user: updateUserParams,
      },
    );

    if (data.error === null) {
      onModalClose();
      fetchUsers();
    }
  };

  const onUpdateUser = () =>
    updateUser().catch(({ response }) => {
      if (response.status === 422) {
        setError(response.data.error);
      }
    });

  return (
    <Modal isVisible={isVisible} onOutsideClick={onModalClose}>
      <div className="col-12 user-modal__edit-header">
        <Modal.Title>
          <I18nText id="user.edit.header.title" />
        </Modal.Title>
        <div
          onClick={onModalClose}
          onKeyDown={onModalClose}
          role="presentation"
        >
          <Icon name="close" size="normal" />
        </div>
      </div>
      <Modal.Content>
        <div className="grid">
          <div className="col-12">
            <Input
              value={firstName}
              onChange={value => setFirstName(value)}
              placeholder={I18n.t('components.user.form.first_name')}
              error={error.firstName}
            />
          </div>
          <div className="col-12">
            <Input
              value={lastName}
              onChange={value => setLastName(value)}
              placeholder={I18n.t('components.user.form.last_name')}
              error={error.lastName}
            />
          </div>
          <div className="col-12">
            <Input
              value={email}
              onChange={value => setEmail(value)}
              placeholder={I18n.t('components.user.form.email')}
              error={error.email}
            />
          </div>
          {(signedInAsSuperAdmin ||
            userInitialAttributes.id === currentUserId) && (
            <div className="col-12">
              <Input
                value={password}
                type="password"
                onChange={value => setPassword(value)}
                placeholder={I18n.t(
                  'components.user.form.password_placeholder',
                )}
                required={false}
              />
            </div>
          )}
          <div className="col-12">
            <SelectBox
              className="user-modal__team-select"
              onChange={onTeamChange}
              onInputChange={fetchTeams}
              options={teams}
              placeholderText={<I18nText id="user.form.teams" />}
              required={false}
              isMulti={true}
              width="full"
              value={selectedTeam}
              errorMsg={error.team}
            />
          </div>
          {!isEditingSelf && (
            <div className="col-12 col-bleed permissions">
              <PermissionsSelection
                permissionOptions={permissionOptions}
                permissionTemplateOptions={permissionTemplateOptions}
                onChange={setPermissions}
                selectedPermissions={permissions}
              />
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className="col-12">
          <div className="user-modal__save">
            <Button
              label={<I18nText id="user.actions.save" />}
              size="small"
              version="v2"
              type="primary"
              onClick={onUpdateUser}
              disabled={isEditingSuperAdmin}
            />
          </div>
          <div className="user-modal__cancel">
            <Button
              label={<I18nText id="user.actions.cancel" />}
              size="small"
              version="v2"
              onClick={onModalClose}
            />
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

EditTable.defaultProps = {
  previousSelectedTeam: null,
  userInitialAttributes: {
    email: null,
  },
  permissionTemplateOptions: [],
  signedInAsSuperAdmin: false,
};

EditTable.propTypes = {
  affiliateId: PropTypes.number.isRequired,
  userInitialAttributes: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    superAdmin: PropTypes.bool.isRequired,
    id: PropTypes.number.isRequired,
  }),
  previousSelectedTeam: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  permissionOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
    .isRequired,
  permissionTemplateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  submitURL: PropTypes.func.isRequired,
  currentUserId: PropTypes.number.isRequired,
  isVisible: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  signedInAsSuperAdmin: PropTypes.bool,
  fetchUsers: PropTypes.func.isRequired,
};

export default EditTable;
